import { FC, useCallback, useEffect, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { CREATE_PRACTICE_TEST } from 'queries/practiceTest'
import { FIND_ONE_SUBJECT } from 'queries/subject'
import { CreatePracticeTestInput, PracticeTestTypeEnum, TimeRecordEnum } from '__generatedTypes__/globalTypes'
import { createPracticeTest, createPracticeTestVariables } from '__generatedTypes__/createPracticeTest'
import { findOneSubject, findOneSubjectVariables } from '__generatedTypes__/findOneSubject'
import { PlatformSlug } from 'constants/consts'
import { useHistory } from 'hooks/useHistory'
import { PageLoader } from 'components/PageLoader'
import { toast } from 'utils/toast'
import { Form } from '../Form'
import { getPTConfig, READING_QB, READING_TYPE_PARAM } from '../consts'

export const Create: FC<{
  type?: PracticeTestTypeEnum
}> = ({ type = PracticeTestTypeEnum.PracticeTest }) => {
  const { navigate } = useLink()
  const { platform } = useParams()
  const [searchParams] = useSearchParams()
  const readingTypeParam = searchParams.get(READING_TYPE_PARAM)
  const subjectId = searchParams.get('subjectId')
  const practiceTestCategoryId = searchParams.get('categoryId')
  const defaultOrder = +(searchParams.get('defaultOrder') ?? 0)
  const { loadingHistory, populateHistoryWithUser } = useHistory()
  const [create] = useMutation<createPracticeTest, createPracticeTestVariables>(CREATE_PRACTICE_TEST)
  const subjectState = useQuery<findOneSubject, findOneSubjectVariables>(FIND_ONE_SUBJECT, {
    variables: {
      id: String(subjectId),
    },
  })
  const subject = subjectState.data?.findOneSubject ?? null

  const config = useMemo(
    () => getPTConfig(type, platform as PlatformSlug, subject, readingTypeParam === READING_QB),
    [type, subject, readingTypeParam, platform]
  )

  const handleSubmit = useCallback(
    async (practiceTest: CreatePracticeTestInput) => {
      if (!config) {
        return
      }

      try {
        const { data } = await create({
          variables: {
            params: {
              ...practiceTest,
              timeRecord: TimeRecordEnum.Timed,
              history: populateHistoryWithUser(),
              isLive: false, // isLive false by default and can be changed only on PTs list
            },
          },
        })

        if (data?.createPracticeTest.id) {
          navigate(config.dashboardLink, undefined, {
            searchParams: new URLSearchParams({
              subjectId: String(subject?.id),
              ...(config.dashboardSearchParams ?? {}),
            }),
          })
          toast.success(`${config.titleSingle} created successfully`)
        } else {
          toast.error(`Failed to create ${config.titleSingle}`)
        }
      } catch (error) {
        toast.error(`Failed to create ${config.titleSingle}`)
      }
    },
    [populateHistoryWithUser, create, navigate, subject, config]
  )

  useEffect(() => {
    if (
      !subjectId ||
      !config ||
      (config?.type === PracticeTestTypeEnum.ReadingQuestionBank && !practiceTestCategoryId) ||
      (!subjectState.loading && !subject)
    ) {
      toast.error(
        `Invalid data provided for creating ${config?.titleSingle ?? 'Test'}, redirecting to ${
          config?.titleMultiple ?? 'Tests'
        } list`
      )
      navigate(config?.dashboardLink ?? cmsRoutes.PRACTICE_TEST_LIST)
    }
  }, [subjectId, subjectState.loading, subject, config])

  if (subjectState.loading || !subject || loadingHistory) {
    return <PageLoader />
  }

  if (!subjectId || !config || (config?.type === PracticeTestTypeEnum.ReadingQuestionBank && !practiceTestCategoryId)) {
    return null
  }

  return (
    <Form
      type={config.type}
      title={config.titleSingle}
      practiceTestOrSubjectId={subjectId}
      practiceTestCategoryId={practiceTestCategoryId ?? undefined}
      subject={subject}
      onSubmit={handleSubmit}
      defaultOrder={defaultOrder}
      questionLocationType={config.questionLocationType}
    />
  )
}
